import { Component, type ErrorInfo, type ReactNode } from 'react';
import { isChunkLoadError } from '@/lib/chunk-recovery';
import { hardReload } from '@/lib/hard-reload';

interface Props {
  children: ReactNode;
  /** 라우트 변경 감지용 키. 값이 바뀌면 에러 상태를 자동으로 리셋한다. */
  resetKey?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * 라우트 레벨 ErrorBoundary.
 * - 전체 화면 대신 콘텐츠 영역 카드 형태로 폴백 UI를 표시한다.
 * - resetKey(보통 useLocation().key)가 바뀌면 에러 상태를 리셋해 정상 자식을 다시 렌더한다.
 * - 최상위 ErrorBoundary(App.tsx)가 최후 방어선이며, 이 컴포넌트는 라우트 단위의 1차 방어선이다.
 * - 청크 로드 실패(배포 후 옛 청크)면 원인 안내 + '다시 불러오기'(hardReload) 하나만 — 자동 복구는 lib/chunk-recovery.ts
 */
export class RouteErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[RouteErrorBoundary]', error, info.componentStack);
  }

  /**
   * resetKey가 변경되면(라우트 이동) 에러 상태를 초기화한다.
   */
  componentDidUpdate(prevProps: Props) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleBack = () => {
    history.back();
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError && isChunkLoadError(this.state.error)) {
      return (
        <div className="flex items-center justify-center min-h-[40vh] p-6">
          <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <h2 className="mb-2 text-base font-semibold text-gray-900 dark:text-gray-100">
              새 버전이 배포되어 화면을 다시 불러와야 합니다
            </h2>
            <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
              열려던 화면이 이전 버전 파일을 찾고 있습니다. 아래 버튼을 누르면 새 버전으로 다시 불러옵니다.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => void hardReload()}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                다시 불러오기
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-[40vh] p-6">
          <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <h2 className="mb-2 text-base font-semibold text-gray-900 dark:text-gray-100">
              페이지를 불러오는 중 오류가 발생했습니다
            </h2>
            <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
              예기치 않은 오류가 발생했습니다. 이전 페이지로 돌아가거나 새로고침해 주세요.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={this.handleBack}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
              >
                뒤로 가기
              </button>
              <button
                type="button"
                onClick={this.handleReload}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                새로고침
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
